// ESC/POS Commands
const ESC = 0x1b;
const GS = 0x1d;

const CMD_INIT = [ESC, 0x40];
const CMD_ALIGN_CENTER = [ESC, 0x61, 0x01];
const CMD_FONT_NORMAL = [ESC, 0x4d, 0x00];
const CMD_FONT_COMPRESSED = [ESC, 0x4d, 0x01];
const CMD_FEED_CUT = [ESC, 0x64, 0x02]; // Feed a little bit to clear the cutter

// Barcode parameters
const CMD_BARCODE_HEIGHT = [GS, 0x68, 0x50]; // Height: 80 units (approx 10mm) - Taller for scanning reliability
const CMD_BARCODE_WIDTH = [GS, 0x77, 0x03];  // Width: 3 units - Thicker bars for instant scanner detection
const CMD_BARCODE_HRI = [GS, 0x48, 0x02];    // Print human-readable text below barcode
const CMD_BARCODE_FONT = [GS, 0x66, 0x01];   // Select small Font B for HRI barcode letters

/**
 * Encodes string to ASCII array
 */
function stringToBytes(str: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < str.length; i++) {
    bytes.push(str.charCodeAt(i) & 0x7f);
  }
  return bytes;
}

/**
 * Compiles a single barcode label.
 */
export function compileWirelessLabel(product: {
  sku: string;
  name: string;
  categoryName?: string;
  price: number;
  brandName?: string;
  mrp?: number;
  discount_percentage?: number;
  retail_price?: number;
}): Uint8Array {
  const bytes: number[] = [];

  // Initialize
  bytes.push(...CMD_INIT);
  bytes.push(...CMD_FONT_COMPRESSED); // 58mm compact layout font

  // Center align text
  bytes.push(...CMD_ALIGN_CENTER);

  // 1. Brand Title Text
  const brand = (product.brandName || 'TEXTILE STORE').toUpperCase();
  bytes.push(...stringToBytes(`${brand}\n`));

  // 2. Product details (Name, SKU, Price)
  const nameTruncated = product.name.substring(0, 20).toUpperCase();
  bytes.push(...stringToBytes(`${nameTruncated}\n`));
  bytes.push(...stringToBytes(`SKU: ${product.sku.toUpperCase()}\n`));

  const mrpVal = product.mrp ?? product.price ?? 0;
  const retailPriceVal = product.retail_price ?? product.price ?? 0;
  
  if (mrpVal > retailPriceVal) {
    bytes.push(...stringToBytes(`MRP: Rs. ${mrpVal.toFixed(2)}\n`));
    bytes.push(...stringToBytes(`SALE: Rs. ${retailPriceVal.toFixed(2)}\n`));
  } else {
    bytes.push(...stringToBytes(`PRICE: Rs. ${retailPriceVal.toFixed(2)}\n`));
  }

  // Spacer before barcode
  bytes.push(...stringToBytes('\n'));

  // 3. ESC/POS Barcode command configuration
  bytes.push(...CMD_BARCODE_HEIGHT);
  bytes.push(...CMD_BARCODE_WIDTH);
  bytes.push(...CMD_BARCODE_HRI);
  bytes.push(...CMD_BARCODE_FONT);

  // Native ESC/POS Barcode directive
  const barcodeData = stringToBytes(product.sku.trim());
  bytes.push(GS, 0x6b, 0x49, barcodeData.length, ...barcodeData);

  // Spacer after barcode
  bytes.push(...stringToBytes('\n'));

  // 4. Dynamic discount / savings summary at bottom if available
  const discountPct = product.discount_percentage ?? (mrpVal > 0 ? Math.round(((mrpVal - retailPriceVal) / mrpVal) * 100) : 0);
  if (discountPct > 0) {
    bytes.push(...CMD_FONT_NORMAL);
    bytes.push(...stringToBytes(`SAVE ${discountPct.toFixed(0)}% ON MRP Rs.${mrpVal.toFixed(0)}\n`));
    bytes.push(...CMD_FONT_COMPRESSED);
  }

  bytes.push(...stringToBytes('--------------------------------\n'));

  // Feed to next label alignment mark
  bytes.push(...CMD_FEED_CUT);

  return new Uint8Array(bytes);
}

/**
 * Combines multiple label payloads into a single print job.
 */
export function compileWirelessLabelsBulk(products: Array<any>): Uint8Array {
  const allBytes: number[] = [];
  products.forEach((product) => {
    const singleLabel = compileWirelessLabel(product);
    allBytes.push(...Array.from(singleLabel));
  });
  return new Uint8Array(allBytes);
}
